import { formatDurationToHrMin } from "@utils/utils";
import React from "react";

import { AnalyticsStatCard } from "@fronterahealth/frontera-ui-components";

import { BcbaSessionAnalyticsVideosDataType } from "@api/graphql/types-and-hooks";
import { CardContainer } from "@pages/VideoAnalytics/components/CardContainer";
import {
  BCBA_ANALYTICS_TITLES,
  COMMENTS_MAPPING,
  ProgressInputData,
  calculatePercentageChange,
  transformToProgressData,
} from "@pages/VideoAnalytics/helpers";

interface CommentsAndFeedbackProps {
  currentData: BcbaSessionAnalyticsVideosDataType | null | undefined;
  previousPeriodData: BcbaSessionAnalyticsVideosDataType | null | undefined;
  isFetching: boolean;
  setIsResponseTimeToRBTQuestionOpen: (open: boolean) => void;
  setIsResponseRateToRBTQuestionOpen: (open: boolean) => void;
  setIsCommentOpen: (open: boolean) => void;
  setIsCommentDistributionOpen: (open: boolean) => void;
}

export const CommentsAndFeedback: React.FC<CommentsAndFeedbackProps> = ({
  currentData,
  previousPeriodData,
  isFetching,
  setIsResponseTimeToRBTQuestionOpen,
  setIsResponseRateToRBTQuestionOpen,
  setIsCommentDistributionOpen,
  setIsCommentOpen,
}) => {
  const commentData = currentData?.commentsData as ProgressInputData;
  const commentsProgressData = currentData?.commentsData
    ? transformToProgressData(commentData, COMMENTS_MAPPING)
    : undefined;
  return (
    <CardContainer title="Comments & Feedback">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2">
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.AVG_RESPONSE_TIME_TO_RBT}
          infoTooltip="The average time it takes for a BCBA to respond to an RBT’s question."
          value={`${formatDurationToHrMin(currentData?.averageResponseTimeToRbtQuestions ?? 0)}`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData ? formatDurationToHrMin(previousPeriodData?.averageResponseTimeToRbtQuestions ?? 0) : "N/A"}`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseTimeToRbtQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseTimeToRbtQuestions ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseTimeToRbtQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseTimeToRbtQuestions ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsResponseTimeToRBTQuestionOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.AVG_RESPONSE_RATE_TO_RBT}
          infoTooltip="The percentage of RBT questions that received a response from a BCBA."
          value={`${currentData?.averageResponseRateToRbtQuestions} %`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageResponseRateToRbtQuestions ?? "N/A"} %` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseRateToRbtQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseRateToRbtQuestions ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageResponseRateToRbtQuestions ?? 0),
                  Number(previousPeriodData?.averageResponseRateToRbtQuestions ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsResponseRateToRBTQuestionOpen(true);
          }}
        />

        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.AVG_COMMENTS_LEFT_PER_VIDEO}
          infoTooltip="The average number of comments left by BCBAs on each video."
          value={`${currentData?.averageCommentLeftByBcbaPerVideo} comments`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.averageCommentLeftByBcbaPerVideo ?? "N/A"} comments` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageCommentLeftByBcbaPerVideo ?? 0),
                  Number(previousPeriodData?.averageCommentLeftByBcbaPerVideo ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageCommentLeftByBcbaPerVideo ?? 0),
                  Number(previousPeriodData?.averageCommentLeftByBcbaPerVideo ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsCommentOpen(true);
          }}
        />
        <AnalyticsStatCard
          title={BCBA_ANALYTICS_TITLES.BREAKDOWN_OF_COMMENTS_LEFT}
          infoTooltip="A categorized view of comments left by BCBAs, such as feedback, questions and praise."
          value={`${currentData?.commentsData?.totalComments} comments`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.commentsData?.totalComments ?? "N/A"} comments` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.commentsData?.totalComments ?? 0),
                  Number(previousPeriodData?.commentsData?.totalComments ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.commentsData?.totalComments ?? 0),
                  Number(previousPeriodData?.commentsData?.totalComments ?? 0),
                ).isPositive
              : undefined
          }
          breakdownData={commentsProgressData}
          loading={isFetching}
          showGraphLink
          onGraphClick={() => {
            setIsCommentDistributionOpen(true);
          }}
        />
      </div>
    </CardContainer>
  );
};
